import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { classifyOperationError } from '../data/errors';
import {
  createMutationLane,
  UnmountedError,
  type MutationLane,
} from '../data/mutationLane';
import type { CustomizationState } from '../state/customizationState';
import { useCustomizationDispatch } from '../state/useCustomizationDispatch';
import { useGetLatestCustomizationState } from '../state/useGetLatestCustomizationState';
import type { RunOperationDeps } from './runOperation';
import { useNotifier } from './useNotifier';
import { useTemplatePermissions } from './useTemplatePermissions';

function readReplicaVersion(state: CustomizationState): number {
  const version = state.replica?.version;

  if (version === undefined) {
    throw new Error('Cannot start a write before the template has loaded');
  }

  return version;
}

// One lane serves every operation of the page, so writes on sections and questions run
// in order and each reads the version its predecessor produced
export function useOperationDeps(): RunOperationDeps {
  const dispatch = useCustomizationDispatch();
  const getLatestState = useGetLatestCustomizationState();
  const notifier = useNotifier();
  const permissions = useTemplatePermissions();

  const [lane] = useState<MutationLane>(() =>
    createMutationLane({
      getVersion: () => readReplicaVersion(getLatestState()),
      classifyFailure: classifyOperationError,
    })
  );

  const isMountedRef = useRef(false);
  const isMounted = useCallback(() => isMountedRef.current, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      lane.abort(getLatestState().templateId, new UnmountedError());
    };
  }, [lane, getLatestState]);

  return useMemo<RunOperationDeps>(
    () => ({
      lane,
      getLatestState,
      dispatch,
      notifier,
      permissions,
      isMounted,
    }),
    [lane, getLatestState, dispatch, notifier, permissions, isMounted]
  );
}
